import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { ErrorState } from '../ui/error-state'
import { LoadingTable } from '../ui/loading-states'
import { Pagination } from '../ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { usePharmaDeadStock } from '../../hooks/usePharmaAnalytics'
import {
  formatCurrency,
  formatIsoDate,
  formatNumber,
} from '../../utils/dateFormat'
import type { PharmaDeadStockFilters } from '../../types/pharma-analytics.types'

interface DeadStockTabProps {
  filters: PharmaDeadStockFilters
  onFiltersChange: (
    update: (prev: PharmaDeadStockFilters) => PharmaDeadStockFilters
  ) => void
}

const WINDOWS = [30, 60, 90, 180, 365]

export function DeadStockTab({ filters, onFiltersChange }: DeadStockTabProps) {
  const { data, isLoading, error, refetch } = usePharmaDeadStock(filters)

  const rows = data?.rows ?? []
  const limit = filters.limit ?? 50
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / limit))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Мёртвый запас</CardTitle>
        <CardDescription>
          Позиции с остатком, по которым не было ни одного движения регистра за
          выбранное окно. Документ «Входящее списание» здесь не учитывается — он
          остаток не двигает.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Без движения:</span>
          {WINDOWS.map((days) => (
            <Button
              key={days}
              size="sm"
              variant={
                (filters.windowDays ?? 60) === days ? 'default' : 'outline'
              }
              onClick={() =>
                onFiltersChange((prev) => ({
                  ...prev,
                  windowDays: days,
                  page: 1,
                }))
              }
            >
              {days} дн.
            </Button>
          ))}
          <div className="ml-auto text-sm tabular-nums">
            <span className="text-muted-foreground">Итого: </span>
            <span className="font-semibold">
              {formatNumber(data?.total ?? 0)} позиций
            </span>
            <span className="text-muted-foreground"> на </span>
            <span className="font-semibold">
              {formatCurrency(data?.totalValue ?? null, { compact: true })}
            </span>
          </div>
        </div>

        {data && !data.historyCovered && (
          // Молчать об этом нельзя: без пометки результат за «365 дней» читался бы
          // как факт, хотя движений в базе накоплено меньше.
          <div className="rounded-md border border-amber-400/40 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-500">
            Истории движений хватает только с {formatIsoDate(data.movementsFrom)} —
            окно шире накопленных данных, часть позиций может оказаться живой.
          </div>
        )}

        {isLoading ? (
          <LoadingTable rows={8} columns={6} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить мёртвый запас"
            description={(error as Error)?.message}
            onAction={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            Позиций без движения не найдено
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Препарат</TableHead>
                    <TableHead>Склад</TableHead>
                    <TableHead>Партия</TableHead>
                    <TableHead>Последнее движение</TableHead>
                    <TableHead className="text-right">Остаток</TableHead>
                    <TableHead className="text-right">Стоимость</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={`${row.warehouseCode}-${row.medicationCode}-${row.batchNumber}`}
                    >
                      <TableCell className="font-medium max-w-xs">
                        <div className="truncate" title={row.medicationName}>
                          {row.medicationName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {row.medicationCode}
                          {row.account && ` · счёт ${row.account}`}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[180px]">
                        <div className="truncate" title={row.warehouseName ?? ''}>
                          {row.warehouseName ?? '—'}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.batchNumber || '—'}
                        {row.expiryDate && (
                          <div className="text-xs text-muted-foreground">
                            до {formatIsoDate(row.expiryDate)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {row.lastMovementDate ? (
                          <>
                            {formatIsoDate(row.lastMovementDate)}
                            <div className="text-xs text-muted-foreground">
                              {formatNumber(row.daysIdle)} дн. назад
                            </div>
                          </>
                        ) : (
                          <Badge variant="secondary" title="За всю сохранённую историю">
                            движений не было
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(row.quantity, { maximumFractionDigits: 3 })}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {formatCurrency(row.value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Pagination
              currentPage={filters.page ?? 1}
              totalPages={totalPages}
              onPageChange={(page) =>
                onFiltersChange((prev) => ({ ...prev, page }))
              }
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
